import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Landmark,
  CreditCard,
  Copy,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankAccountForm = ({ account, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    bank_name: account?.bank_name || '',
    account_number: account?.account_number || '',
    account_holder: account?.account_holder || ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bank_name || !formData.account_number || !formData.account_holder) {
      toast.error('Mohon isi semua field');
      return;
    }

    setLoading(true);
    try {
      if (account?.id) {
        await axios.put(`${API_URL}/api/bank-accounts/${account.id}`, formData);
        toast.success('Rekening bank berhasil diperbarui');
      } else {
        await axios.post(`${API_URL}/api/bank-accounts`, formData);
        toast.success('Rekening bank berhasil ditambahkan');
      }
      onSave();
    } catch (error) {
      console.error('Error saving bank account:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan rekening bank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="bank_name">Nama Bank *</Label>
        <Input
          id="bank_name"
          name="bank_name"
          placeholder="Contoh: BCA, Mandiri, BRI"
          value={formData.bank_name}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="bank-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="account_number">Nomor Rekening *</Label>
        <Input
          id="account_number"
          name="account_number"
          placeholder="Nomor rekening"
          value={formData.account_number}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="account-number-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="account_holder">Atas Nama *</Label>
        <Input
          id="account_holder"
          name="account_holder"
          placeholder="Nama pemilik rekening"
          value={formData.account_holder}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="account-holder-input"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="bank-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : account?.id ? 'Simpan' : 'Tambah'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const RekeningBank = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-accounts?active_only=false`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      toast.error('Gagal memuat data rekening bank');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setDialogOpen(true);
  };

  const handleDelete = (account) => {
    setSelectedAccount(account);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/bank-accounts/${selectedAccount.id}`);
      toast.success('Rekening bank berhasil dihapus');
      fetchAccounts();
    } catch (error) {
      toast.error('Gagal menghapus rekening bank');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedAccount(null);
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    fetchAccounts();
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Nomor rekening disalin');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const activeAccounts = accounts.filter(a => a.is_active);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="rekening-bank-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Rekening Bank</h1>
          <p className="text-muted-foreground mt-1">Kelola rekening bank untuk pembayaran</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-bank-btn">
          <Plus className="w-5 h-5 mr-2" />
          Tambah Rekening
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Landmark className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold">{activeAccounts.length}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Rekening Aktif</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold">{accounts.length}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Rekening</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="p-4 border-blue-200 bg-blue-50">
        <div className="flex items-start gap-3">
          <Landmark className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Informasi Pembayaran</p>
            <p className="text-sm text-blue-700 mt-1">
              Rekening bank aktif akan ditampilkan di halaman Pembayaran dan di kwitansi untuk memudahkan penghuni melakukan transfer.
            </p>
          </div>
        </div>
      </Card>

      {/* Bank Accounts Table */}
      {loading ? (
        <Card className="p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        </Card>
      ) : accounts.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <Landmark className="empty-state-icon" />
            <p className="empty-state-title">Belum ada rekening bank</p>
            <p className="empty-state-description">Tambahkan rekening bank untuk menerima pembayaran</p>
            <Button onClick={() => setDialogOpen(true)} className="mt-4 rounded-xl">
              <Plus className="w-5 h-5 mr-2" />
              Tambah Rekening
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Bank</TableHead>
                <TableHead>Nomor Rekening</TableHead>
                <TableHead>Atas Nama</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id} data-testid={`bank-row-${account.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Landmark className="w-5 h-5 text-primary" />
                      </div>
                      <span className="font-medium">{account.bank_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 rounded bg-muted text-sm font-mono">
                        {account.account_number}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(account.account_number, account.id)}
                        data-testid={`copy-btn-${account.id}`}
                      >
                        {copiedId === account.id ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>{account.account_holder}</TableCell>
                  <TableCell>
                    <Badge className={account.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                      {account.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`bank-menu-${account.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(account)} data-testid={`edit-bank-${account.id}`}>
                          <Pencil className="w-4 h-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(account)} className="text-destructive" data-testid={`delete-bank-${account.id}`}>
                          <Trash2 className="w-4 h-4 mr-2" />Hapus
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedAccount ? 'Edit Rekening Bank' : 'Tambah Rekening Bank'}
            </DialogTitle>
            <DialogDescription>
              {selectedAccount ? 'Perbarui data rekening bank' : 'Tambahkan rekening bank untuk menerima pembayaran'}
            </DialogDescription>
          </DialogHeader>
          <BankAccountForm
            account={selectedAccount}
            onSave={handleSave}
            onClose={() => { setDialogOpen(false); setSelectedAccount(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Rekening Bank?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus rekening {selectedAccount?.bank_name} - {selectedAccount?.account_number}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl" data-testid="confirm-delete-bank">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RekeningBank;
